import { createContext, ReactNode } from 'react'

import config from 'config'
import useSessionStorage from 'hooks/useSessionStorage'
import moment from 'moment'
import { getCurrentDate, getWeekAgoDate } from 'utils/getDates'

import { Attribution, CustomizationProps } from 'types'

// initial state
const initialState: CustomizationProps = {
  ...config,
  onChangeSampleData: () => {},
  onChangeAttr: (attr: Attribution) => {},
  onChangeWorkspace: (id: string, currency: string, timezone: string) => {},
  onChangeDrawerOpen: (drawerOpen: boolean) => {},
  onChangeDate: (startDate: Date, endDate: Date) => {},
  onChangeColumn: (key: string, value: any) => {},
}

// ==============================|| CONFIG CONTEXT & PROVIDER ||============================== //

const ConfigContext = createContext(initialState)

type ConfigProviderProps = {
  children: ReactNode
}

function ConfigProvider({ children }: ConfigProviderProps) {
  const [config, setConfig] = useSessionStorage('weberlo-config', initialState)

  const onChangeWorkspace = (
    id: string,
    currency: string,
    timezone: string
  ) => {
    setConfig({
      ...config,
      workspace: id,
      currency,
      timezone,
    })
  }

  const onChangeCurrency = (id: string) => {
    setConfig({
      ...config,
      currency: id,
    })
  }

  const onChangeDrawerOpen = (drawerOpen: boolean) => {
    setConfig({
      ...config,
      drawerOpen,
    })
  }

  const onChangeSampleData = () => {
    setConfig({
      ...config,
      sampleData: !config.sampleData,
    })
  }

  const onChangeAttr = (attr: Attribution) => {
    setConfig({
      ...config,
      attr,
    })
  }

  const onChangeDate = (startDate: Date, endDate: Date) => {
    setConfig({
      ...config,
      startDate: moment(startDate).startOf('day').format(),
      endDate: moment(endDate).endOf('day').format(),
    })
  }

  const onChangeColumn = (key: string, value: any) => {
    setConfig({
      ...config,
      column:  {
        ...config.column,
        [key]: value,
      }
    })
  }

  // Default to the last 7 days until a range is picked, so queries never see empty dates
  const hasRange = !!config.startDate && !!config.endDate
  const startDate = hasRange
    ? config.startDate
    : moment(new Date(getWeekAgoDate(config.timezone)))
        .startOf('day')
        .format()
  const endDate = hasRange
    ? config.endDate
    : moment(new Date(getCurrentDate(config.timezone)))
        .endOf('day')
        .format()

  return (
    <ConfigContext.Provider
      value={{
        ...config,
        startDate,
        endDate,
        onChangeColumn,
        onChangeWorkspace,
        onChangeCurrency,
        onChangeDrawerOpen,
        onChangeSampleData,
        onChangeDate,
        onChangeAttr,
      }}>
      {children}
    </ConfigContext.Provider>
  )
}

export { ConfigProvider, ConfigContext }
